import {SpotlightSection_CampaignElement} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {useHistory} from '@aboutyou/router';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  higherDesktopAndUpLinaria,
  InfoCircleWithDialog,
  largeScreensLinaria,
  SlideAmbientProps,
  tabletAndUpLinaria,
  Text,
} from '@aboutyou/ui-components';
import {getColorShade} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {FC, useMemo} from 'react';
import {useSplitFirstProductIds} from '../../hooks/useSplitFirstProductIds';
import DealHintDarkRight from '../assets/deal-hint-dark-right.svg';
import DealHintDark from '../assets/deal-hint-dark.svg';
import DealHintLightRight from '../assets/deal-hint-light-right.svg';
import DealHintLight from '../assets/deal-hint-light.svg';
import {imageCardStyles, StyledCountdownChip} from './Shared';
import {SlidePropsType} from './types';

export const CampaignSlide: FC<
  {
    campaign: SpotlightSection_CampaignElement;
  } & SlidePropsType & {SlideUIComponent: FC<SlideAmbientProps>}
> = ({
  campaign,
  isActive,
  isCloseToViewport,
  onSlideClick,
  'data-testid': dataTestid,
  SlideUIComponent,
  uniqueKey = '',
  ...rest
}) => {
  const history = useHistory();
  const colorShade = getColorShade(campaign.backgroundColor.hex);

  const {linkWithoutFirstProductIds, fullLink} = useSplitFirstProductIds(
    campaign.linkTarget.url,
  );

  const onBannerTargetClick = () => {
    if (fullLink && fullLink !== linkWithoutFirstProductIds) {
      history.push(fullLink);
    }
  };

  const productImages = useMemo(
    () =>
      campaign.productGallery.map(product => ({
        url: product.image.url,
        description: product.image.description,
      })),
    [campaign.productGallery],
  );

  return (
    <SlideUIComponent
      {...rest}
      uniqueKey={uniqueKey}
      data-testid={dataTestid}
      bannerTarget={{
        url: linkWithoutFirstProductIds || campaign.linkTarget.url,
        external: campaign.linkTarget.external,
        onClick: onBannerTargetClick,
      }}
      subline={campaign.topline}
      headline={campaign.headline}
      productImages={productImages}
      isActive={Boolean(isActive)}
      isCloseToViewport={Boolean(isCloseToViewport)}
      onSlideClick={onSlideClick}
      floatingChip={<StyledCountdownChip expiryDate={campaign.endsAt} />}
      customSlideImage={
        <CampaignImage
          $backgroundColor={campaign.backgroundColor.hex}
          data-theme={colorShade}
        />
      }
      slideContentLabels={
        <SlideLabel data-reduce-font={Boolean(campaign.categoryLinks)}>
          {campaign.campaignText}
        </SlideLabel>
      }
      floatingTopRight={
        campaign.conditionText && (
          <StyledInfoCircleWithDialog
            conditions={campaign.conditionText}
            fill={campaign.backgroundColor.hex}
            endsAt={campaign.endsAt}
            countdownBackgroundColor={campaign.backgroundColor.hex}
            onOpenCallback={rest.onPauseAutomaticSlide}
            onCloseCallback={rest.onResumeAutomaticSlide}
          />
        )
      }
      linkTargets={campaign.categoryLinks}
    />
  );
};

const CampaignImage = styled.div<{$backgroundColor: string}>`
  ${imageCardStyles}

  background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0.08%,
      rgba(0, 0, 0, 0.04) 25.61%,
      rgba(0, 0, 0, 0.15) 41.99%,
      rgba(0, 0, 0, 0.4) 96.1%
    ),
    ${props => props.$backgroundColor ?? ''};

  &::after {
    content: '';
    background-image: url(${DealHintDark});
    background-repeat: no-repeat;
    background-size: 40%;
    position: absolute;
    inset: 0;
  }

  &[data-theme='dark']::after {
    // we use the opposite color from the given shade to get contrast
    background-image: url(${DealHintLight});
  }

  ${tabletAndUpLinaria(`
    aspect-ratio: initial;
    width: 100%;
    box-shadow: unset;

      &::after {
        background-size: 20%;
      }

      &::before {
        content: '';
        background-image: url(${DealHintDarkRight});
        background-repeat: no-repeat;
        background-size: 30%;
        background-position: top right;
        position: absolute;
        inset: 0;
      }

      &[data-theme='dark']::before {
        background-image: url(${DealHintLightRight});
      }
  `)}
`;

const SlideLabel = styled(Text)`
  font-size: ${FONT_SIZES.px24};
  font-size: ${FONT_WEIGHTS.mediumHeavy};
  color: ${COLORS['Text.White']};
  line-height: 1.42;
  margin-bottom: 24px;
  display: flex;

  && {
    ${largeScreensLinaria(`
      font-size: ${FONT_SIZES.px56};
      font-weight: ${FONT_WEIGHTS.mediumHeavy};
      line-height: 1.1;
    `)}
  }

  &&[data-reduce-font='true'] {
    ${tabletAndUpLinaria(`margin-bottom: 0;`)}

    ${higherDesktopAndUpLinaria(`margin-bottom: 24px;`)}

    ${largeScreensLinaria(`
      font-size: ${FONT_SIZES.px46};
    `)}
  }
`;

const StyledInfoCircleWithDialog = styled(InfoCircleWithDialog)`
  &&& {
    top: 8px;
    right: 8px;
    z-index: 2;

    &::after {
      content: '';
      background-color: #ffffff;
      position: absolute;
      inset: 1px;
      z-index: -1;
      border-radius: 50%;
    }

    svg {
      filter: brightness(1.1) saturate(1.2);
    }

    ${tabletAndUpLinaria(`
      top: 16px;
      right: 16px;

      svg {
        filter: opacity(0.7);
      }
    `)}
  }
`;
